import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import {
  Badge,
  Box,
  Dialog,
  IconButton,
  styled,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import {
  DateCalendar,
  LocalizationProvider,
  PickersDay,
} from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs, { Dayjs } from "dayjs";
import React, { useState } from "react";

import DropDownIcon from "../../../assets/icons/black_down_arrow.svg";
import CalendarIcon from "../../../assets/icons/calendar-green.svg";
import { datePickerModalStyles as styles } from "../../../styles/Modals/DatePickerModal.style";

const CustomBadge = styled(Badge)(({ invisible }) => ({
  "& .MuiBadge-dot": {
    bottom: 0,
    left: "50%",
    transform: "translateX(-50%)",
    display: invisible ? "none" : "block",
  },
}));

interface DatePickerProps {
  date: Dayjs;
  handleDateChange: (date: Dayjs) => void;
  bookableDates?: string[];
  isCalendarVisible: boolean;
  setIsCalendarVisible: React.Dispatch<React.SetStateAction<boolean>>;
}

const DatePicker: React.FC<DatePickerProps> = ({
  date,
  handleDateChange,
  bookableDates,
  isCalendarVisible,
  setIsCalendarVisible,
}) => {
  const theme = useTheme();

  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [baseMonth, setBaseMonth] = useState(dayjs().startOf("month"));

  const CustomDay = (props: any) => {
    const { day, outsideCurrentMonth, ...rest } = props;

    const formatted = day.format("YYYY-MM-DD");
    const isToday = dayjs().isSame(day, "day");
    const isMarked = bookableDates?.some(
      (bd) => dayjs(bd).format("YYYY-MM-DD") === formatted
    );
    const isSelected = day.isSame(date, "day")

    if (outsideCurrentMonth) {
      return (
        <PickersDay
          day={day}
          outsideCurrentMonth={outsideCurrentMonth}
          {...rest}
        />
      );
    }

    return (
      <CustomBadge
        overlap="circular"
        variant="dot"
        invisible={!isMarked}
        color="success"
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      >
        <PickersDay
          day={day}
          {...rest}
          sx={[
            styles.customDay(isToday),
            isSelected && styles.selectedDay,
          ]}
        />
      </CustomBadge>
    );
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box sx={styles.datePickerBox} onClick={() => setIsCalendarVisible(true)}>
        <img src={CalendarIcon} alt="" />
        <Typography sx={styles.dateText(theme)}>
          {date?.format("ddd, MMM D, YYYY")}
        </Typography>
        <img src={DropDownIcon} style={styles.dropdownIcon} alt="" />
      </Box>

      <Dialog
        open={isCalendarVisible}
        onClose={() => setIsCalendarVisible(false)}
        sx={styles.calendarDialog}
      >
        {!isMobile && (
            <Box sx={styles.dialogHeader(theme)}>
              <IconButton
                  size="small"
                  onClick={() => setBaseMonth((m) => m.subtract(1, "month"))}
              >
                  <ChevronLeftIcon />
              </IconButton>

              <Typography fontWeight={500}>
                  {baseMonth.format("MMMM YYYY")} –{" "}
                  {baseMonth.add(1, "month").format("MMMM YYYY")}
              </Typography>

              <IconButton
                  size="small"
                  onClick={() => setBaseMonth((m) => m.add(1, "month"))}
              >
                  <ChevronRightIcon />
              </IconButton>
            </Box>
        )}

        <Box sx={styles.calendarContainer}>
          {/* LEFT calendar */}
          <DateCalendar
            key={baseMonth.format("YYYY-MM") + "-left"}
            referenceDate={baseMonth}
            value={null}
            onChange={(d) => {
              handleDateChange(d);
              setIsCalendarVisible(false);
            }}
            disablePast
            minDate={dayjs()}
            slots={{ day: CustomDay }}
            views={["day"]}
            sx={{
                ...styles.dateCalendar,
                "& .MuiPickersArrowSwitcher-root": {
                    display: { md: "none", sm: "block" },
                },
            }}
          />

          {/* RIGHT calendar (next month) */}
          {!isMobile && (
            <DateCalendar
                key={baseMonth.format("YYYY-MM") + "-right"}
                referenceDate={baseMonth.clone().add(1, "month")}
                value={null}
                onChange={(d) => {
                  handleDateChange(d);
                  setIsCalendarVisible(false);
                }}
                disablePast
                minDate={dayjs()}
                slots={{ day: CustomDay }}
                views={["day"]}
                sx={{
                  ...styles.dateCalendar,
                  "& .MuiPickersArrowSwitcher-root": { visibility: "hidden" },
                }}
            />
          )}
        </Box>
      </Dialog>
    </LocalizationProvider>
  );
};

export default DatePicker;
